import type { LucideIcon } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { TileIcon, TileLabel, homeTileButtonClass } from '@features/dashboard/TileIcon';
import { PanicSosBadge } from '@features/panic/PanicSosBadge';

/**
 * One entry of the home Quick Links set, flattened from every source that feeds
 * it — tenant tiles, `more_categories` groups and the permission-gated admin
 * shortcuts — so Home and the Quick links picker render one list.
 */
export interface QuickLinkItem {
  /** Stable id; also the key the member's Home picks are saved by. */
  id: string;
  label: string;
  imageUrl?: string;
  /** Lucide fallback when the link has no module image. */
  fallbackIcon?: LucideIcon;
  isPanic?: boolean;
  /** Permission-gated admin shortcut, reachable only from quick links. */
  isAdmin?: boolean;
  onSelect: () => void;
}

/** Quick-link tile chrome, shared by the Home grid and the Customize home preview. */
export function QuickLinkTile({
  link,
  onSelect,
}: {
  link: QuickLinkItem;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button type="button" onClick={onSelect} className={homeTileButtonClass}>
      {link.isPanic ? (
        <span className="flex h-full w-full items-center justify-center">
          <PanicSosBadge className="h-12 w-12 text-sm" />
        </span>
      ) : (
        <>
          <TileIcon imageUrl={link.imageUrl} label={link.label} fallbackIcon={link.fallbackIcon} />
          <TileLabel label={link.label} />
        </>
      )}
    </button>
  );
}

/**
 * A link's icon in a small tinted chip, for list rows (Explore, search, the
 * Quick links picker). Panic shows its red SOS disc instead.
 */
export function QuickLinkIcon({
  link,
  className,
}: {
  link: Pick<QuickLinkItem, 'label' | 'imageUrl' | 'fallbackIcon' | 'isPanic'>;
  className?: string;
}): JSX.Element {
  if (link.isPanic) return <PanicSosBadge className={cn('h-9 w-9 text-[0.625rem]', className)} />;

  return (
    <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] bg-primary-50', className)}>
      <TileIcon
        imageUrl={link.imageUrl}
        label={link.label}
        fallbackIcon={link.fallbackIcon}
        className="h-5 w-5 text-body opacity-100"
      />
    </span>
  );
}
